import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { queryKeys, useAuthenticatedClient } from "./queries";
import type { components } from "./schema";

export type Supplier = components["schemas"]["SupplierRead"];
export type SupplierKind = Supplier["kind"];
export type SourcingOption =
  components["schemas"]["ManufacturerSourcingOptionRead"];
export type SourcingOptionWrite =
  components["schemas"]["ManufacturerSourcingOptionWrite"];
type ManufacturerDetail = components["schemas"]["ManufacturerDetailRead"];

export const SUPPLIER_KIND_LABELS: Record<SupplierKind, string> = {
  distributor: "Distributor",
  rep_group: "Rep group",
  buying_group: "Buying group",
  online_retailer: "Online retailer",
};

/** Suppliers that sell to us themselves, at their own prices (POs name them "c/o" the manufacturer). */
export const CARE_OF_KINDS: ReadonlySet<SupplierKind> = new Set([
  "distributor",
  "online_retailer",
]);

const suppliersKey = ["suppliers"] as const;

export function useSuppliers() {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: suppliersKey,
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/suppliers", {});
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateSupplier() {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: components["schemas"]["SupplierCreate"]) => {
      const client = await getClient();
      const { data, error } = await client.POST("/dashboard/suppliers", {
        body,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (supplier) => {
      queryClient.setQueryData<Supplier[]>(suppliersKey, (old) =>
        old
          ? [...old, supplier].sort((a, b) => a.name.localeCompare(b.name))
          : old,
      );
      queryClient.invalidateQueries({ queryKey: suppliersKey });
    },
  });
}

/** A supplier's address and phone, shared by every manufacturer bought through it. */
export function useUpdateSupplier(manuUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manuUuid);
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: components["schemas"]["SupplierUpdate"] & { id: number }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/suppliers/{supplier_id}",
        { params: { path: { supplier_id: id } }, body },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ id, ...fields }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ManufacturerDetail>(detailKey);
      queryClient.setQueryData<ManufacturerDetail>(
        detailKey,
        (old) =>
          old && {
            ...old,
            sourcing_options: old.sourcing_options.map((option) =>
              option.supplier?.id === id
                ? {
                    ...option,
                    supplier: {
                      ...option.supplier,
                      address: { ...option.supplier.address, ...fields },
                    } as Supplier,
                  }
                : option,
            ),
          },
      );
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous)
        queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: suppliersKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all });
    },
  });
}

function useInvalidateManufacturer(manuUuid: string) {
  const queryClient = useQueryClient();
  return () =>
    queryClient.invalidateQueries({
      queryKey: queryKeys.manufacturers.detail(manuUuid),
    });
}

export function useCreateSourcingOption(manuUuid: string) {
  const getClient = useAuthenticatedClient();
  const invalidate = useInvalidateManufacturer(manuUuid);
  return useMutation({
    mutationFn: async (body: SourcingOptionWrite) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/manufacturers/{uuid}/sourcing-options",
        {
          params: { path: { uuid: manuUuid } },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onSettled: invalidate,
  });
}

export function useUpdateSourcingOption(manuUuid: string) {
  const getClient = useAuthenticatedClient();
  const invalidate = useInvalidateManufacturer(manuUuid);
  return useMutation({
    mutationFn: async ({
      optionId,
      ...body
    }: { optionId: number } & SourcingOptionWrite) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/manufacturers/{uuid}/sourcing-options/{option_id}",
        { params: { path: { uuid: manuUuid, option_id: optionId } }, body },
      );
      if (error) throw error;
      return data;
    },
    onSettled: invalidate,
  });
}

/** Removes the option (and the people who order through it) optimistically. */
export function useDeleteSourcingOption(manuUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manuUuid);
  return useMutation({
    mutationFn: async (optionId: number) => {
      const client = await getClient();
      const { error } = await client.DELETE(
        "/dashboard/manufacturers/{uuid}/sourcing-options/{option_id}",
        { params: { path: { uuid: manuUuid, option_id: optionId } } },
      );
      if (error) throw error;
    },
    onMutate: async (optionId) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ManufacturerDetail>(detailKey);
      queryClient.setQueryData<ManufacturerDetail>(
        detailKey,
        (old) =>
          old && {
            ...old,
            sourcing_options: old.sourcing_options.filter(
              (option) => option.id !== optionId,
            ),
            contact_links: old.contact_links?.filter(
              (link) => link.sourcing_option_id !== optionId,
            ),
          },
      );
      return { previous };
    },
    onError: (_error, _optionId, context) => {
      if (context?.previous)
        queryClient.setQueryData(detailKey, context.previous);
    },
    // Removing the preferred source promotes the next one, whose terms carry to open POs.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: detailKey }),
  });
}

/** Reorders options (preferred first) optimistically. */
export function useReorderSourcingOptions(manuUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manuUuid);
  return useMutation({
    mutationFn: async (optionIds: number[]) => {
      const client = await getClient();
      const { data, error } = await client.PUT(
        "/dashboard/manufacturers/{uuid}/sourcing-options/order",
        {
          params: { path: { uuid: manuUuid } },
          body: { option_ids: optionIds },
        },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async (optionIds) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ManufacturerDetail>(detailKey);
      queryClient.setQueryData<ManufacturerDetail>(detailKey, (old) => {
        if (!old) return old;
        const byId = new Map(
          old.sourcing_options.map((option) => [option.id, option]),
        );
        return {
          ...old,
          sourcing_options: optionIds.flatMap((id, index) => {
            const option = byId.get(id);
            return option ? [{ ...option, rank: index + 1 }] : [];
          }),
        };
      });
      return { previous };
    },
    onError: (_error, _optionIds, context) => {
      if (context?.previous)
        queryClient.setQueryData(detailKey, context.previous);
    },
    // A new preferred source's terms carry to open POs.
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: detailKey }),
  });
}

export function useAddSourcingContact(manuUuid: string) {
  const getClient = useAuthenticatedClient();
  const invalidate = useInvalidateManufacturer(manuUuid);
  return useMutation({
    mutationFn: async (
      body: components["schemas"]["ManufacturerContactLinkCreate"],
    ) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/manufacturers/{uuid}/contacts",
        {
          params: { path: { uuid: manuUuid } },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onSettled: invalidate,
  });
}

type SourcingSaveBody = Omit<SourcingOptionWrite, "terms"> & {
  terms?: Record<string, unknown>;
};

/**
 * Saves one source's fields or terms optimistically and rolls back on failure.
 * The preferred source's terms are the manufacturer's, so the whole detail refetches.
 */
export function useSaveSourcingOption(manuUuid: string, optionId: number) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manuUuid);
  const patch = async (body: SourcingSaveBody) => {
    const client = await getClient();
    const { data, error } = await client.PATCH(
      "/dashboard/manufacturers/{uuid}/sourcing-options/{option_id}",
      {
        params: { path: { uuid: manuUuid, option_id: optionId } },
        body: body as SourcingOptionWrite,
      },
    );
    if (error) throw error;
    return data;
  };
  // Terms changes carry to the manufacturer's open POs and their action items.
  const refreshPurchaseOrders = (body: SourcingSaveBody) => {
    if (body.terms != null)
      queryClient.invalidateQueries({ queryKey: queryKeys.purchaseOrders.all });
  };
  // Multi-field panels keep their own draft, so a refused save must leave the cache untouched.
  const panelMutation = useMutation({
    mutationFn: patch,
    onSuccess: (_data, body) => {
      queryClient.invalidateQueries({ queryKey: detailKey });
      refreshPurchaseOrders(body);
    },
  });
  const mutation = useMutation({
    mutationFn: patch,
    onSuccess: (_data, body) => refreshPurchaseOrders(body),
    onMutate: async (body) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ManufacturerDetail>(detailKey);
      const { terms, ...rest } = body;
      const fields = Object.fromEntries(
        Object.entries(rest).filter(([key]) => key !== "discounts"),
      );
      queryClient.setQueryData<ManufacturerDetail>(
        detailKey,
        (old) =>
          old && {
            ...old,
            sourcing_options: old.sourcing_options.map((option) =>
              option.id === optionId
                ? ({ ...option, ...fields, ...(terms ?? {}) } as SourcingOption)
                : option,
            ),
          },
      );
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous)
        queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: detailKey }),
  });
  const variables = mutation.variables;
  const isSaving = (field: string) =>
    mutation.isPending &&
    variables != null &&
    (field in variables ||
      (variables.terms != null && field in variables.terms));
  const save = (body: SourcingSaveBody, label: string) =>
    mutation.mutate(body, {
      onError: (error) => {
        const detail =
          error &&
          typeof error === "object" &&
          "detail" in error &&
          typeof error.detail === "string"
            ? error.detail
            : `Could not save ${label}`;
        toast.error(detail);
      },
    });
  return {
    save,
    saveTerms: (terms: Record<string, unknown>, label: string) =>
      save({ terms }, label),
    isSaving,
    termsMutation: {
      mutate: (
        terms: Record<string, unknown>,
        options: { onSuccess?: () => void; onError?: () => void },
      ) => panelMutation.mutate({ terms }, options),
      isPending: panelMutation.isPending,
    },
  };
}

type FreightRuleBody = components["schemas"]["ManufacturerFreightRuleCreate"];

/** A backup source's stored freight program (the preferred source's is the manufacturer's). */
export function useCreateSourceFreightRule(manuUuid: string, optionId: number) {
  const getClient = useAuthenticatedClient();
  const invalidate = useInvalidateManufacturer(manuUuid);
  return useMutation({
    mutationFn: async (body: FreightRuleBody) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/manufacturers/{uuid}/sourcing-options/{option_id}/freight-rules",
        {
          params: { path: { uuid: manuUuid, option_id: optionId } },
          body: { ...body, priority: body.priority ?? 0 },
        },
      );
      if (error) throw error;
      return data;
    },
    onSettled: invalidate,
  });
}

export function useUpdateSourceFreightRule(manuUuid: string, optionId: number) {
  const getClient = useAuthenticatedClient();
  const invalidate = useInvalidateManufacturer(manuUuid);
  return useMutation({
    mutationFn: async ({
      ruleId,
      body,
    }: {
      ruleId: number;
      body: components["schemas"]["ManufacturerFreightRuleUpdate"];
    }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/manufacturers/{uuid}/sourcing-options/{option_id}/freight-rules/{rule_id}",
        {
          params: {
            path: { uuid: manuUuid, option_id: optionId, rule_id: ruleId },
          },
          body,
        },
      );
      if (error) throw error;
      return data;
    },
    onSettled: invalidate,
  });
}

export function useDeleteSourceFreightRule(manuUuid: string, optionId: number) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = queryKeys.manufacturers.detail(manuUuid);
  return useMutation({
    mutationFn: async (ruleId: number) => {
      const client = await getClient();
      const { error } = await client.DELETE(
        "/dashboard/manufacturers/{uuid}/sourcing-options/{option_id}/freight-rules/{rule_id}",
        {
          params: {
            path: { uuid: manuUuid, option_id: optionId, rule_id: ruleId },
          },
        },
      );
      if (error) throw error;
    },
    onMutate: async (ruleId) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<ManufacturerDetail>(detailKey);
      queryClient.setQueryData<ManufacturerDetail>(
        detailKey,
        (old) =>
          old && {
            ...old,
            sourcing_options: old.sourcing_options.map((option) =>
              option.id === optionId
                ? {
                    ...option,
                    freight_rules: (option.freight_rules ?? []).filter(
                      (rule) => rule.id !== ruleId,
                    ),
                  }
                : option,
            ),
          },
      );
      return { previous };
    },
    onError: (_error, _ruleId, context) => {
      if (context?.previous)
        queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: detailKey }),
  });
}

export type SpecialProgram = components["schemas"]["SpecialProgramRead"];

/** Run a specials program through a supplier or buying group (null: every source). */
export function useSetSpecialProgramSupplier(manuUuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      program,
      supplierId,
    }: {
      program: string;
      supplierId: number | null;
    }) => {
      const client = await getClient();
      const { data, error } = await client.PUT(
        "/dashboard/specials/programs/{program}",
        {
          params: { path: { program } },
          body: { supplier_id: supplierId },
        },
      );
      if (error) throw error;
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.manufacturers.detail(manuUuid),
      });
      queryClient.invalidateQueries({ queryKey: ["specials"] });
    },
  });
}

// ── Supplier pages (USA-1535) ────────────────────────────────────────────────

export type SupplierListItem = components["schemas"]["SupplierListRead"];
export type SupplierDetail = components["schemas"]["SupplierDetailRead"];
export type SupplierSourcing = components["schemas"]["SupplierSourcingRead"];
export type SupplierPerson = components["schemas"]["SupplierPersonRead"];

const supplierDetailKey = (uuid: string) => ["suppliers", "detail", uuid] as const;

export function useSupplier(uuid: string) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: supplierDetailKey(uuid),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/suppliers/{uuid}", {
        params: { path: { uuid } },
      });
      if (error) throw error;
      return data;
    },
  });
}

/** Edit a supplier from its own page: name, kind, email domain, address and phone. */
export function useEditSupplier(uuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = supplierDetailKey(uuid);
  return useMutation({
    mutationFn: async ({
      id,
      ...body
    }: components["schemas"]["SupplierUpdate"] & { id: number }) => {
      const client = await getClient();
      const { data, error } = await client.PATCH(
        "/dashboard/suppliers/{supplier_id}",
        { params: { path: { supplier_id: id } }, body },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async (fields) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<SupplierDetail>(detailKey);
      const { name, kind, email_domain, phone, address1, address2, city, state, zip_code } = fields;
      const address = { phone, address1, address2, city, state, zip_code };
      queryClient.setQueryData<SupplierDetail>(
        detailKey,
        (old) =>
          old && {
            ...old,
            ...(name != null ? { name } : {}),
            ...(kind != null ? { kind } : {}),
            ...(email_domain !== undefined ? { email_domain } : {}),
            ...(fields.spec_credit_project_minimum !== undefined
              ? {
                  spec_credit_rule:
                    fields.spec_credit_project_minimum === null
                      ? null
                      : { project_minimum: String(fields.spec_credit_project_minimum) },
                }
              : {}),
            address: {
              ...old.address,
              ...Object.fromEntries(
                Object.entries(address).filter(([, value]) => value !== undefined),
              ),
            },
          },
      );
      return { previous };
    },
    onError: (_error, _body, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: suppliersKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all });
    },
  });
}

/** Fold a duplicate supplier into another of the same kind; the duplicate is deleted. */
export function useMergeSupplier() {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, intoId }: { id: number; intoId: number }) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/suppliers/{supplier_id}/merge",
        {
          params: { path: { supplier_id: id } },
          body: { into_supplier_id: intoId },
        },
      );
      if (error) throw error;
      return data;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: suppliersKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all });
    },
  });
}

/** Delete a supplier no manufacturer buys through. */
export function useDeleteSupplier() {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const client = await getClient();
      const { error } = await client.DELETE("/dashboard/suppliers/{supplier_id}", {
        params: { path: { supplier_id: id } },
      });
      if (error) throw error;
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: suppliersKey });
    },
  });
}

/** What a supplier covers, e.g. "29 preferred · 6 backup" or "258 in program". */
export function supplierCoverage(supplier: SupplierListItem): string {
  if (supplier.kind === "buying_group") {
    return supplier.buying_group_for
      ? `${supplier.buying_group_for} in program`
      : "No manufacturers";
  }
  const parts = [
    supplier.preferred_for ? `${supplier.preferred_for} preferred` : null,
    supplier.backup_for ? `${supplier.backup_for} backup` : null,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : "No manufacturers";
}

/** Add someone at the supplier in a role; every manufacturer bought through it uses them. */
export function useAddSupplierContact(uuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      supplierId,
      ...body
    }: components["schemas"]["SupplierContactCreate"] & { supplierId: number }) => {
      const client = await getClient();
      const { data, error } = await client.POST(
        "/dashboard/suppliers/{supplier_id}/contacts",
        { params: { path: { supplier_id: supplierId } }, body },
      );
      if (error) throw error;
      return data;
    },
    onSuccess: (detail) => queryClient.setQueryData(supplierDetailKey(uuid), detail),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: suppliersKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all });
    },
  });
}

/** Remove someone from the supplier and from every manufacturer bought through it. */
export function useRemoveSupplierContact(uuid: string) {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  const detailKey = supplierDetailKey(uuid);
  return useMutation({
    mutationFn: async ({ supplierId, contactId }: { supplierId: number; contactId: number }) => {
      const client = await getClient();
      const { data, error } = await client.DELETE(
        "/dashboard/suppliers/{supplier_id}/contacts/{supplier_contact_id}",
        { params: { path: { supplier_id: supplierId, supplier_contact_id: contactId } } },
      );
      if (error) throw error;
      return data;
    },
    onMutate: async ({ contactId }) => {
      await queryClient.cancelQueries({ queryKey: detailKey });
      const previous = queryClient.getQueryData<SupplierDetail>(detailKey);
      queryClient.setQueryData<SupplierDetail>(
        detailKey,
        (old) =>
          old && { ...old, contacts: (old.contacts ?? []).filter((person) => person.id !== contactId) },
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(detailKey, context.previous);
    },
    onSuccess: (detail) => queryClient.setQueryData(detailKey, detail),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: suppliersKey });
      queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all });
    },
  });
}

export type SourcingCategories =
  components["schemas"]["ManufacturerSourcingCategoriesRead"];
export type SourcingCategory = components["schemas"]["SourcingCategoryRead"];

const sourcingCategoriesKey = (manuUuid: string, poUuid?: string) =>
  [...queryKeys.manufacturers.detail(manuUuid), "sourcing-categories", poUuid ?? null] as const;

/**
 * The store categories a manufacturer's products are in and which ones each backup source is
 * used for (USA-1506); with a PO, also that PO's categories.
 */
export function useSourcingCategories(manuUuid: string, poUuid?: string) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: sourcingCategoriesKey(manuUuid, poUuid),
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET(
        "/dashboard/manufacturers/{uuid}/sourcing-categories",
        {
          params: {
            path: { uuid: manuUuid },
            query: poUuid ? { purchase_order: poUuid } : {},
          },
        },
      );
      if (error) throw error;
      return data;
    },
    enabled: Boolean(manuUuid),
  });
}

/** Set the categories a backup source is used for; an empty list means staff pick it by hand. */
export function useSetSourceCategories(manuUuid: string) {
  const getClient = useAuthenticatedClient();
  const invalidate = useInvalidateManufacturer(manuUuid);
  return useMutation({
    mutationFn: async ({
      optionId,
      taxonomyNodeIds,
    }: {
      optionId: number;
      taxonomyNodeIds: number[];
    }) => {
      const client = await getClient();
      const { data, error } = await client.PUT(
        "/dashboard/manufacturers/{uuid}/sourcing-options/{option_id}/categories",
        {
          params: { path: { uuid: manuUuid, option_id: optionId } },
          body: { taxonomy_node_ids: taxonomyNodeIds },
        },
      );
      if (error) throw error;
      return data;
    },
    onSettled: invalidate,
  });
}

// ── Sources across manufacturers (USA-1716) ──────────────────────────────────

export type SourceRow = components["schemas"]["SourceRowRead"];
export type SourcePerson = components["schemas"]["SourcePersonRead"];
export type SourceBulkAction = components["schemas"]["SourceBulkWrite"]["action"];
export type SourceBulkResult = components["schemas"]["SourceBulkResult"];

export interface SourceFilters {
  search?: string;
  supplier?: string;
  contact_id?: number;
  position?: "preferred" | "backup";
  order_method?: "email" | "online" | "other";
  kind?: "direct" | "rep_group" | "distributor" | "online_retailer" | "buying_group";
  no_po_contact?: boolean;
}

const sourcesKey = ["sources"] as const;

export function useSources(filters: SourceFilters, page: number) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: [...sourcesKey, "list", filters, page],
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/sources", {
        params: { query: { ...filters, page, limit: 50 } },
      });
      if (error) throw error;
      return data;
    },
    placeholderData: (previous) => previous,
  });
}

export function useSourcePeople() {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: [...sourcesKey, "people"],
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/sources/people", {});
      if (error) throw error;
      return data;
    },
    staleTime: 60_000,
  });
}

/** Every source the filters match, to select them across pages. */
export function useSelectAllSources(filters: SourceFilters) {
  const getClient = useAuthenticatedClient();
  return useMutation({
    mutationFn: async () => {
      const client = await getClient();
      const { data, error } = await client.GET("/dashboard/sources/ids", { params: { query: filters } });
      if (error) throw error;
      return data;
    },
  });
}

/** What a change would do; saves nothing. */
export function useSourcePreview(body: {
  action: SourceBulkAction;
  option_ids: number[];
  contact_id?: number;
} | null) {
  const getClient = useAuthenticatedClient();
  return useQuery({
    queryKey: [...sourcesKey, "preview", body],
    enabled: body !== null,
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      const client = await getClient();
      const { data, error } = await client.POST("/dashboard/sources/bulk", {
        body: { ...body!, dry_run: true },
      });
      if (error) throw error;
      return data;
    },
  });
}

export function useSourceChange() {
  const getClient = useAuthenticatedClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { action: SourceBulkAction; option_ids: number[]; contact_id?: number }) => {
      const client = await getClient();
      const { data, error } = await client.POST("/dashboard/sources/bulk", {
        body: { ...body, dry_run: false },
      });
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sourcesKey });
      void queryClient.invalidateQueries({ queryKey: suppliersKey });
      void queryClient.invalidateQueries({ queryKey: queryKeys.manufacturers.all });
    },
    onError: () => toast.error("Couldn't change these sources"),
  });
}
